import type { Item, Tag, Invite, CalendarImportSource, GoogleCalendarCandidate } from '@chaos-concierge/shared';
import type { CreateItemInput, DeviceReminder, ItemPatch, PlannerStore, SpaceMember, SpaceRole, SpaceWithMembers } from './types';
import { LocalStore } from './LocalStore';

type QueuedOp =
  | { op: 'create'; tempId: string; input: CreateItemInput }
  | { op: 'update'; id: string; patch: ItemPatch }
  | { op: 'remove'; id: string }
  | { op: 'deviceReminder'; deviceId: string; itemId: string; reminder: string | null };

const QUEUE_KEY = 'planner:sync-queue';
const CACHE_KEY = 'planner:items';

function loadQueue(): QueuedOp[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? (JSON.parse(raw) as QueuedOp[]) : [];
  } catch {
    return [];
  }
}

function saveQueue(q: QueuedOp[]): void {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
}

function loadCache(): Item[] {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Item[]) : [];
  } catch {
    return [];
  }
}

function saveCache(items: Item[]): void {
  localStorage.setItem(CACHE_KEY, JSON.stringify(items));
}

function newTempId(): string {
  return 'tmp_' + String(Date.now()) + Math.random().toString(36).slice(2, 7);
}

/**
 * Wraps ApiStore with offline-first behaviour:
 *  - list()   → returns the local cache immediately; refreshes from API in background when online
 *  - create/update/remove → apply to local cache immediately; queue for API sync when offline
 *  - On reconnect, flushes the queue to the API
 */
export class OfflineSyncStore implements PlannerStore {
  private local = new LocalStore();

  constructor(private remote: PlannerStore) {
    window.addEventListener('online', () => this.flush());
  }

  // ── public API ────────────────────────────────────────────────────────────
  // list() returns every calendar's items merged (the server does the merge);
  // each item carries spaceId so the UI can overlay/colour them. Offline-first
  // behaviour is identical for personal and shared items — the server figures
  // out which calendar an item lives on when a queued op is flushed.

  cachedList(): Item[] {
    return loadCache();
  }

  async refresh(): Promise<Item[]> {
    await this.flush();
    const items = await this.remote.list();
    saveCache(items);
    return items;
  }

  async list(): Promise<Item[]> {
    if (navigator.onLine) {
      try {
        // Flush any queued mutations first so the server state is fresh
        await this.flush();
        const items = await this.remote.list();
        saveCache(items);
        return items;
      } catch {
        // Fall through to cache
      }
    }
    return loadCache();
  }

  async create(input: CreateItemInput): Promise<Item> {
    const tempId = newTempId();
    // Apply locally first so the UI is instant
    const localItem = await this.local.create(input);
    const withTempId = { ...localItem, id: tempId };
    const cached = loadCache();
    saveCache([...cached, withTempId]);

    if (navigator.onLine) {
      try {
        const created = await this.remote.create(input);
        // Replace temp item with real one
        saveCache(loadCache().map((i) => (i.id === tempId ? created : i)));
        return created;
      } catch (e) {
        saveCache(loadCache().filter((i) => i.id !== tempId));
        throw e;
      }
    }

    const q = loadQueue();
    q.push({ op: 'create', tempId, input });
    saveQueue(q);
    return withTempId;
  }

  async update(id: string, patch: ItemPatch): Promise<Item> {
    // Apply locally
    const cached = loadCache();
    const idx = cached.findIndex((i) => i.id === id);
    const previous = idx !== -1 ? cached[idx] : null;
    if (idx !== -1) {
      cached[idx] = { ...cached[idx], ...patch, updatedAt: Date.now() };
      saveCache(cached);
    }

    if (navigator.onLine) {
      try {
        const updated = await this.remote.update(id, patch);
        saveCache(loadCache().map((i) => (i.id === id ? updated : i)));
        return updated;
      } catch (e) {
        if (previous) {
          saveCache(loadCache().map((i) => (i.id === id ? previous : i)));
        }
        throw e;
      }
    }

    const q = loadQueue();
    // Merge with any existing pending update for the same id
    const existing = q.findIndex((o) => o.op === 'update' && o.id === id);
    if (existing !== -1 && q[existing].op === 'update') {
      q[existing] = { op: 'update', id, patch: { ...(q[existing] as { patch: ItemPatch }).patch, ...patch } };
    } else {
      q.push({ op: 'update', id, patch });
    }
    saveQueue(q);

    return cached[idx] ?? ({ id, ...patch } as Item);
  }

  async remove(id: string): Promise<void> {
    // Apply locally
    const cached = loadCache();
    const filtered = cached.filter((i) => i.id !== id && i.parentId !== id);
    saveCache(filtered);

    if (navigator.onLine) {
      try {
        await this.remote.remove(id);
        return;
      } catch (e) {
        saveCache(cached);
        throw e;
      }
    }

    // Remove any pending ops for this id, then enqueue the delete
    const q = loadQueue().filter((o) => {
      if (o.op === 'create' && o.tempId === id) return false;
      if (o.op === 'update' && o.id === id) return false;
      if (o.op === 'remove' && o.id === id) return false;
      return true;
    });
    q.push({ op: 'remove', id });
    saveQueue(q);
  }

  // ── tags (online-only, simple passthrough) ───────────────────────────────

  async listTags(): Promise<Tag[]> {
    if (navigator.onLine) {
      try {
        return await this.remote.listTags();
      } catch {
        // fall through to local
      }
    }
    return this.local.listTags();
  }

  createTag(name: string, color: string): Promise<Tag> {
    return navigator.onLine ? this.remote.createTag(name, color) : this.local.createTag(name, color);
  }

  updateTag(id: string, patch: Partial<Pick<Tag, 'name' | 'color'>>): Promise<Tag> {
    return navigator.onLine ? this.remote.updateTag(id, patch) : this.local.updateTag(id, patch);
  }

  async removeTag(id: string): Promise<void> {
    return navigator.onLine ? this.remote.removeTag(id) : this.local.removeTag(id);
  }

  // ── shared spaces (online-only passthrough) ────────────────────────────────

  listSpaces(): Promise<SpaceWithMembers[]> {
    return this.remote.listSpaces();
  }

  createSpace(name: string, color: string): Promise<SpaceWithMembers> {
    return this.remote.createSpace(name, color);
  }

  deleteSpace(spaceId: string): Promise<void> {
    return this.remote.deleteSpace(spaceId);
  }

  leaveSpace(spaceId: string): Promise<void> {
    return this.remote.leaveSpace(spaceId);
  }

  inviteToSpace(spaceId: string, email: string): Promise<Invite> {
    return this.remote.inviteToSpace(spaceId, email);
  }

  revokeSpaceInvite(spaceId: string, email: string): Promise<void> {
    return this.remote.revokeSpaceInvite(spaceId, email);
  }

  removeSpaceMember(spaceId: string, userId: string): Promise<void> {
    return this.remote.removeSpaceMember(spaceId, userId);
  }

  updateSpaceMemberRole(spaceId: string, userId: string, role: Exclude<SpaceRole, 'owner'>): Promise<SpaceMember> {
    return this.remote.updateSpaceMemberRole(spaceId, userId, role);
  }

  listInvites(): Promise<Invite[]> {
    return this.remote.listInvites();
  }

  acceptInvite(spaceId: string): Promise<SpaceWithMembers> {
    return this.remote.acceptInvite(spaceId);
  }

  declineInvite(spaceId: string): Promise<void> {
    return this.remote.declineInvite(spaceId);
  }

  // ── calendar imports (online-only passthrough) ──────────────────────────────

  listCalendarImports(): Promise<CalendarImportSource[]> {
    return this.remote.listCalendarImports();
  }

  startGoogleCalendarConnect(): Promise<{ authUrl: string }> {
    return this.remote.startGoogleCalendarConnect();
  }

  finishGoogleCalendarConnect(code: string, state: string): Promise<{ accountEmail: string; calendars: GoogleCalendarCandidate[] }> {
    return this.remote.finishGoogleCalendarConnect(code, state);
  }

  listGoogleCalendarCandidates(): Promise<GoogleCalendarCandidate[]> {
    return this.remote.listGoogleCalendarCandidates();
  }

  disconnectGoogleCalendarAccount(accountEmail: string): Promise<void> {
    return this.remote.disconnectGoogleCalendarAccount(accountEmail);
  }

  createGoogleCalendarImport(input: Pick<GoogleCalendarCandidate, 'accountEmail' | 'calendarId'>): Promise<CalendarImportSource> {
    return this.remote.createGoogleCalendarImport(input);
  }

  updateCalendarImport(importId: string, patch: Partial<Pick<CalendarImportSource, 'privacy' | 'includeDeclined' | 'includeAllDay'>>): Promise<CalendarImportSource> {
    return this.remote.updateCalendarImport(importId, patch);
  }

  syncCalendarImport(importId: string): Promise<CalendarImportSource> {
    return this.remote.syncCalendarImport(importId);
  }

  removeCalendarImport(importId: string): Promise<void> {
    return this.remote.removeCalendarImport(importId);
  }

  getPushConfig(): Promise<{ enabled: boolean; publicKey: string | null }> {
    return this.remote.getPushConfig();
  }

  savePushSubscription(input: Parameters<PlannerStore['savePushSubscription']>[0]): Promise<{ subscribed: boolean }> {
    return this.remote.savePushSubscription(input);
  }

  removePushSubscription(endpoint: string): Promise<{ subscribed: boolean }> {
    return this.remote.removePushSubscription(endpoint);
  }

  async listDeviceReminders(deviceId: string): Promise<DeviceReminder[]> {
    if (navigator.onLine) {
      try {
        return await this.remote.listDeviceReminders(deviceId);
      } catch {
        // fall through to device-local cache
      }
    }
    return this.local.listDeviceReminders(deviceId);
  }

  async setDeviceReminder(deviceId: string, itemId: string, reminder: string | null): Promise<DeviceReminder> {
    const localReminder = await this.local.setDeviceReminder(deviceId, itemId, reminder);
    if (navigator.onLine) {
      try {
        return await this.remote.setDeviceReminder(deviceId, itemId, reminder);
      } catch {
        // fall through to queue below
      }
    }
    const q = loadQueue();
    const existing = q.findIndex((o) => o.op === 'deviceReminder' && o.deviceId === deviceId && o.itemId === itemId);
    if (existing !== -1) q[existing] = { op: 'deviceReminder', deviceId, itemId, reminder };
    else q.push({ op: 'deviceReminder', deviceId, itemId, reminder });
    saveQueue(q);
    return localReminder;
  }

  // ── sync ──────────────────────────────────────────────────────────────────

  async flush(): Promise<void> {
    const q = loadQueue();
    if (q.length === 0) return;

    // Map from tempId → real id, resolved as we process creates
    const idMap = new Map<string, string>();

    const remaining: QueuedOp[] = [];

    for (const op of q) {
      try {
        if (op.op === 'create') {
          const created = await this.remote.create(op.input);
          idMap.set(op.tempId, created.id);
          // Replace temp item in cache
          saveCache(loadCache().map((i) => (i.id === op.tempId ? created : i)));
        } else if (op.op === 'update') {
          const realId = idMap.get(op.id) ?? op.id;
          const updated = await this.remote.update(realId, op.patch);
          saveCache(loadCache().map((i) => (i.id === realId ? updated : i)));
        } else if (op.op === 'remove') {
          const realId = idMap.get(op.id) ?? op.id;
          await this.remote.remove(realId);
          // Already removed from cache at the time of the op
        } else if (op.op === 'deviceReminder') {
          const realId = idMap.get(op.itemId) ?? op.itemId;
          await this.remote.setDeviceReminder(op.deviceId, realId, op.reminder);
        }
      } catch {
        remaining.push(op);
      }
    }

    saveQueue(remaining);
  }
}
